import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { IcoBell } from "./icons";
import { notificationsApi } from "../api/endpoints";
import { userDisplayName } from "../lib/userAvatar";
import type { NotificationItem } from "../types/domain";

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => notificationsApi.list({ limit: 12 }),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
  const items = query.data?.notifications ?? [];
  const unread = query.data?.unreadCount ?? 0;

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const openNotification = (n: NotificationItem) => {
    setOpen(false);
    if (n.taskId) {
      const qs = new URLSearchParams({ taskId: n.taskId, notificationId: n.id });
      navigate(`/tasks?${qs.toString()}`);
    }
  };

  return (
    <div className="notification-wrap" ref={wrapRef}>
      <button
        type="button"
        className={"notification-btn" + (unread > 0 ? " has-unread" : "")}
        onClick={() => setOpen((v) => !v)}
        aria-label="Уведомления"
        title="Уведомления"
      >
        <IcoBell size={16} />
        {unread > 0 && <span className="notification-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="notification-popover">
          <div className="notification-head">
            <b>Уведомления</b>
            {unread > 0 && (
              <button
                type="button"
                className="notification-read-all"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
              >
                Прочитать все
              </button>
            )}
          </div>
          {query.isLoading ? (
            <div className="notification-empty">Загружаем…</div>
          ) : items.length === 0 ? (
            <div className="notification-empty">Пока ничего нет.</div>
          ) : (
            <div className="notification-list">
              {items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={"notification-item" + (!n.readAt ? " is-unread" : "")}
                  onClick={() => openNotification(n)}
                >
                  <span className="notification-item-title">
                    {userDisplayName(n.actor?.name ?? null, n.actor?.email ?? "Кто-то")} упомянул вас
                  </span>
                  {n.commentPreview && (
                    <span className="notification-item-text">{n.commentPreview}</span>
                  )}
                  <span className="notification-item-time">
                    {formatNotificationTime(n.createdAt)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatNotificationTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
